import { useState } from 'react';
import {
  filterPropertiesByListingMode,
  isPropertyFavorited,
  isPropertyComparing,
  propertyMatchesListedDateFilter,
  isPropertyOpenForPrimaryInvestment,
  propertyVisibleOnSite,
} from '../utils/properties';
import BrowseListedDateFilter from '../components/listings/BrowseListedDateFilter';
import { propertyMatchesCountry, propertyMatchesSearch, getRegionLabel } from '../data/countries';
import useListPagination from '../hooks/useListPagination';
import PropertyCard from '../components/property/PropertyCard';
import ListingsMarketHeader from '../components/listings/ListingsMarketHeader';
import ListingsViewToggle from '../components/listings/ListingsViewToggle';
import ListingsMapBrowse from '../components/listings/ListingsMapBrowse';
import HomeMarketingSections from '../components/home/HomeMarketingSections';
import PaginationBar from '../components/shared/PaginationBar';

const HomePage = ({ properties, favorites, compareList, onToggleFav, onToggleCompare, onSelectProperty, onContactAgent, country, onCountryChange, onViewListings, loading, setPage, isLoggedIn }) => {
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [listingsView, setListingsView] = useState("grid");
  const [listedDateMode, setListedDateMode] = useState("all");
  const [listedYear, setListedYear] = useState("");
  const [listedMonth, setListedMonth] = useState("");
  const [listedDate, setListedDate] = useState("");
  const saleProperties = filterPropertiesByListingMode(properties, "sale");
  const filtered = saleProperties.filter(p => {
    const matchesFilter = filter === "All" || p.type === filter || (filter === "Land" && p.landBanking);
    const matchesSearch = propertyMatchesSearch(p, search);
    const matchesCountry = propertyMatchesCountry(p.country, country);
    const matchesListedDate = propertyMatchesListedDateFilter(p, {
      mode: listedDateMode,
      year: listedYear,
      month: listedMonth,
      date: listedDate,
    });
    return matchesFilter && matchesSearch && matchesCountry && matchesListedDate
      && propertyVisibleOnSite(p)
      && isPropertyOpenForPrimaryInvestment(p);
  });
  const paginationKey = `home|${filter}|${search}|${country}|${listedDateMode}|${listedYear}|${listedMonth}|${listedDate}`;
  const {
    items: pageItems,
    page,
    total,
    totalPages,
    startIndex,
    endIndex,
    hasPrev,
    hasNext,
    goToPage
  } = useListPagination(filtered, paginationKey);

  return (
    <>
      <ListingsMarketHeader
        country={country}
        onViewListings={() => onViewListings({ listingMode: "all", search })}
        search={search}
        onSearchChange={setSearch}
        landing
      />
      {loading && (
        <div className="container" style={{textAlign:"center",padding:"2rem"}}>
          <i className="fas fa-spinner fa-spin" style={{fontSize:"2rem",color:"var(--primary)"}}></i>
          <p style={{color:"var(--text-light)",marginTop:"0.5rem"}}>Loading properties...</p>
        </div>
      )}
      <div className="container listings-section" id="home-listings" data-listings-anchor>
        <div className="section-header">
          <div className="section-header-top">
            <h2 className="section-title">View listings — {getRegionLabel(country)}</h2>
            <div className="listings-meta">
              <span className="listings-count">{filtered.length} {filtered.length === 1 ? "property" : "properties"}</span>
              <ListingsViewToggle view={listingsView} onChange={setListingsView} />
            </div>
          </div>
          <div className="listings-toolbar">
            <div className="filters listings-scroll-strip">
              {["All","Residential","Commercial","Vacation","Land"].map(f=>
                <button key={f} className={`filter-btn ${filter===f?"active":""}`} onClick={()=>setFilter(f)}>{f}</button>
              )}
            </div>
            <BrowseListedDateFilter
              variant="home"
              dateMode={listedDateMode}
              onDateModeChange={setListedDateMode}
              listedYear={listedYear}
              onListedYearChange={setListedYear}
              listedMonth={listedMonth}
              onListedMonthChange={setListedMonth}
              listedDate={listedDate}
              onListedDateChange={setListedDate}
              onClear={() => {
                setListedYear("");
                setListedMonth("");
                setListedDate("");
              }}
            />
          </div>
        </div>
        {filtered.length === 0 ? (
          <div style={{textAlign:"center",padding:"3rem 1rem",background:"var(--card)",borderRadius:"var(--radius)",border:"1px solid var(--border)"}}>
            <i className="fas fa-globe" style={{fontSize:"2.5rem",color:"var(--text-light)",marginBottom:"1rem"}}></i>
            <h3 style={{marginBottom:"0.5rem"}}>No properties found</h3>
            <p style={{color:"var(--text-light)",maxWidth:"480px",margin:"0 auto"}}>
              {search.trim()
                ? `No listings in ${getRegionLabel(country)} match "${search}". Try another city or clear the search.`
                : listedDateMode !== "all"
                  ? `No listings in ${getRegionLabel(country)} match the selected listed date. Try another period or clear the date filter.`
                  : `No listings in ${getRegionLabel(country)} yet. Open View listings to change market and filters.`}
            </p>
          </div>
        ) : (
          <>
            {listingsView === "map" ? (
              <ListingsMapBrowse
                properties={pageItems}
                onSelectProperty={onSelectProperty}
                onContactAgent={onContactAgent}
                country={country}
              />
            ) : (
              <div className="properties-grid">
                {pageItems.map(p => (
                  <PropertyCard key={p.id} p={p} isFavorite={isPropertyFavorited(favorites, p.id)}
                    isComparing={isPropertyComparing(compareList, p.id)}
                    onToggleFav={onToggleFav} onToggleCompare={onToggleCompare}
                    onClick={()=>onSelectProperty(p)} country={country} />
                ))}
              </div>
            )}
            <PaginationBar
              page={page}
              totalPages={totalPages}
              total={total}
              startIndex={startIndex}
              endIndex={endIndex}
              hasPrev={hasPrev}
              hasNext={hasNext}
              onPageChange={goToPage}
              itemLabel="properties"
            />
          </>
        )}
      </div>
      <HomeMarketingSections
        setPage={setPage}
        isLoggedIn={isLoggedIn}
        onViewListings={() => onViewListings({ listingMode: "all", search })}
      />
    </>
  );
};

export default HomePage;
